import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * FileUpload Component
 * Handles XLSX file upload with drag-and-drop and template download
 */
import { useCallback, useState, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUpload, faFileExcel, faSpinner, faDownload } from "@fortawesome/pro-solid-svg-icons";
import { Button } from "@perfectserve/library";
import { downloadTemplate } from "../utils/templateGenerator";
export const FileUpload = ({ onFileSelect, isLoading = false, error = null }) => {
    const [isDragging, setIsDragging] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [validationOnly, setValidationOnly] = useState(false);
    const fileInputRef = useRef(null);
    const handleDragOver = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    }, []);
    const handleDragLeave = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    }, []);
    const handleDrop = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        const files = e.dataTransfer.files;
        if (files.length > 0) {
            const file = files[0];
            if (file.name.endsWith(".xlsx") ||
                file.name.endsWith(".xls") ||
                file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet") {
                setSelectedFile(file);
                onFileSelect(file, validationOnly);
            }
        }
    }, [onFileSelect, validationOnly]);
    const handleFileInput = useCallback((e) => {
        const files = e.target.files;
        if (files && files.length > 0) {
            setSelectedFile(files[0]);
            onFileSelect(files[0], validationOnly);
        }
    }, [onFileSelect, validationOnly]);
    const handleClick = useCallback(() => {
        fileInputRef.current?.click();
    }, []);
    const handleDownloadTemplate = useCallback(() => {
        downloadTemplate();
    }, []);
    return (_jsxs("div", { className: "auto-provisioning__file-upload", children: [_jsxs("div", { className: `auto-provisioning__dropzone ${isDragging ? "dragging" : ""} ${isLoading ? "loading" : ""}`, onDragOver: handleDragOver, onDragLeave: handleDragLeave, onDrop: handleDrop, onClick: handleClick, children: [_jsx("input", { ref: fileInputRef, type: "file", accept: ".xlsx,.xls", onChange: handleFileInput, style: { display: "none" } }), isLoading ? (_jsxs("div", { className: "auto-provisioning__dropzone-content", children: [_jsx(FontAwesomeIcon, { icon: faSpinner, spin: true, className: "auto-provisioning__dropzone-icon" }), _jsx("p", { children: "Parsing file..." })] })) : selectedFile ? (_jsxs("div", { className: "auto-provisioning__dropzone-content", children: [_jsx(FontAwesomeIcon, { icon: faFileExcel, className: "auto-provisioning__dropzone-icon success" }), _jsx("p", { children: selectedFile.name }), _jsx("span", { className: "auto-provisioning__dropzone-hint", children: "Click or drag to replace" })] })) : (_jsxs("div", { className: "auto-provisioning__dropzone-content", children: [_jsx(FontAwesomeIcon, { icon: faUpload, className: "auto-provisioning__dropzone-icon" }), _jsx("span", { className: "auto-provisioning__dropzone-hint", children: "Click to browse or drag and drop to the browser" })] }))] }), error && _jsx("div", { className: "auto-provisioning__error", children: error }), _jsx("div", { className: "auto-provisioning__options", children: _jsxs("label", { className: "auto-provisioning__checkbox-label", children: [_jsx("input", { type: "checkbox", checked: validationOnly, onChange: (e) => setValidationOnly(e.target.checked) }), _jsx("span", { children: "Validation Only" })] }) }), _jsxs("div", { className: "auto-provisioning__template-section", children: [_jsx("p", { children: "Need a template?" }), _jsxs(Button, { variant: "secondary", onClick: handleDownloadTemplate, children: [_jsx(FontAwesomeIcon, { icon: faDownload }), "Download Template"] })] })] }));
};
export default FileUpload;
